import React, { useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Card, DataTable, hasActiveFilters } from "@sscs/ui";
import { getQueryStatus, sortItems } from "@sscs/utils";
import { useReportDispatch, useReportSelector } from "../../hooks";
import { useGetComponentVulnerabilitiesQuery } from "../../services/fileBrowser.api";
import { filterUpdated, paginationUpdated, selectFilter, selectPagination, selectSort, sortUpdated, } from "../store/vulnerabilitiesSlice";
import { useGetFileAndReportId } from "../useGetFileAndReportId";
import Filter from "./filter/Filter";
import { filterVulnerabilities, initFilter, specialCasesForSorting, } from "./utils";
import Vulnerability from "./vulnerability/Vulnerability";
const COLUMNS = [
    { id: "info", label: "Info", width: "70px" },
    {
        id: "cvss_score",
        label: "CVSS score",
        sortProperty: "cvss_score",
        sortId: "vulnerabilities-sort-cvss-score",
        startReverse: true,
        width: "135px",
    },
    {
        id: "cve",
        label: "CVE identifier",
        sortProperty: "cve",
        sortId: "vulnerabilities-sort-cve",
        startReverse: true,
        width: "185px",
    },
    {
        id: "introduced",
        label: "Introduced in",
        sortProperty: "introduced",
        sortId: "vulnerabilities-sort-introduced",
        width: "225px",
    },
    {
        id: "resolved",
        label: "Resolved in",
        sortProperty: "resolved",
        sortId: "vulnerabilities-sort-resolved",
        startReverse: true,
        width: "225px",
    },
    { id: "triage", label: "Triage", width: "200px" },
    { id: "tags", label: "Tags", width: "100%" },
];
const Vulnerabilities = () => {
    const dispatch = useReportDispatch();
    const { fileId, reportId } = useGetFileAndReportId();
    const [focusedRow, setFocusedRow] = useState(null);
    const { data = { active: [], triaged: [] }, isLoading, isFetching, isError, error, } = useGetComponentVulnerabilitiesQuery(fileId
        ? {
            reportId: reportId,
            fileId: fileId,
        }
        : skipToken);
    const vulnerabilities = useMemo(() => data.active.concat(data.triaged), [data]);
    const cveList = useMemo(() => vulnerabilities.map((vulnerability) => ({
        cve: vulnerability.cve,
        triaged: vulnerability.exploit.includes("TRIAGED"),
    })), [vulnerabilities]);
    const filter = useReportSelector(selectFilter);
    const sort = useReportSelector(selectSort);
    const pagination = useReportSelector(selectPagination);
    const filteredData = useMemo(() => filterVulnerabilities(vulnerabilities, filter), [vulnerabilities, filter]);
    const sortedData = useMemo(() => {
        return sortItems(filteredData, {
            property: sort.property,
            reverse: sort.reverse,
        }, specialCasesForSorting);
    }, [filteredData, sort]);
    const paginatedData = useMemo(() => {
        return sortedData.slice((pagination.currentPage - 1) * pagination.itemsPerPage, pagination.currentPage * pagination.itemsPerPage);
    }, [sortedData, pagination]);
    if (!fileId)
        return null;
    return (React.createElement(React.Fragment, null,
        React.createElement(Card.Filters, { found: {
                id: "vulnerabilities-clear-all-filters",
                count: sortedData.length,
                initialFilters: initFilter,
                activeFilters: filter,
                onClear: () => dispatch(filterUpdated(initFilter)),
            } },
            React.createElement(Filter, { filter: filter, cveList: cveList, onChange: (filter) => dispatch(filterUpdated(filter)) })),
        React.createElement(DataTable, { chrome: "plain", size: "large", columns: COLUMNS, sort: sort, onSortChange: (sort) => dispatch(sortUpdated(sort)), empty: {
                subject: "vulnerabilities",
                filters: {
                    active: hasActiveFilters(initFilter, filter),
                    onReset: () => dispatch(filterUpdated(initFilter)),
                },
            }, status: getQueryStatus({
                data,
                isLoading,
                isFetching,
                isError,
                error,
            }), pagination: {
                id: "vulnerabilities-pagination",
                itemCount: sortedData.length,
                pagination,
                onChange: (pagination) => dispatch(paginationUpdated(pagination)),
            } }, paginatedData.map((vulnerability) => (React.createElement(Vulnerability, { key: vulnerability.cve, vulnerability: vulnerability, isFocused: focusedRow === vulnerability.cve, onExpandedRowClick: () => setFocusedRow(vulnerability.cve), onExploitClick: (exploit) => dispatch(filterUpdated({ ...filter, exploit: [exploit] })) }))))));
};
export default Vulnerabilities;
//# sourceMappingURL=Vulnerabilities.js.map